import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./ConfirmSearchChangeModal.module.scss";
import { Button } from "src/components";
import { Cross, Loader } from "src/assets/icons";
import { useModal, useKeyboardListener } from "src/hooks";

type Props = {
  onConfirm: () => Promise<void>;
  onCancel: () => void;
};

export const ConfirmSearchChangeModal: FC<Props> = ({
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    if (isLoading) return;
    onCancel();
  };

  useKeyboardListener({
    key: "Escape",
    type: "keydown",
    callback: handleClose,
  });

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      closeModal("confirm-search-change");
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.title}>
          <span>{t("component.modal.confirm_search_change.title")}</span>
        </div>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          className={styles.closeButton}
          buttonSize="small"
          buttonStyle="outlined"
        >
          <Cross />
        </Button>
      </div>

      <div className={styles.content}>
        <p className={styles.description}>
          {t("component.modal.confirm_search_change.message")}
        </p>
      </div>

      <div className={styles.buttonsWrapper}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          buttonStyle="outlined"
          className={styles.cancelButton}
        >
          {t("component.modal.confirm_search_change.button.cancel")}
        </Button>

        <Button
          onClick={handleConfirm}
          disabled={isLoading}
          className={styles.confirmButton}
        >
          {isLoading ? (
            <Loader className={styles.loader} />
          ) : (
            t("component.modal.confirm_search_change.button.confirm")
          )}
        </Button>
      </div>
    </div>
  );
};
